"use client";

import { type ReactNode, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Button } from "@/components/ui/design-system/button";
import { Input } from "@/components/ui/design-system/input";
import { Label } from "@/components/ui/design-system/label";
import { Body, Display } from "@/components/ui/design-system/text";
import { sharedAuthClient } from "@/lib/sharedAuthClient";

export interface PasswordSignInCopy {
  headline: ReactNode;
  lede: ReactNode;
  emailLabel: string;
  passwordLabel: string;
  submit: string;
  signingIn: string;
  errors: { rejected: string; offline: string };
}

/**
 * Sign-in with an email and a password, for the few logins an engineer
 * creates for someone who can't receive an emailed code: an app directory's
 * reviewer (scripts/reviewerLogin.ts). Everyone else signs in with a code
 * (SignInForm), so this is the screen behind "Use a password instead".
 */
export function PasswordSignInForm({
  copy,
  idPrefix,
  footer,
  onSignedIn,
}: {
  copy: PasswordSignInCopy;
  idPrefix: string;
  footer?: ReactNode;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    setPending(true);
    setError(null);
    try {
      const result = await sharedAuthClient.signIn.email({
        email: email.trim().toLowerCase(),
        password,
      });
      if (result.error) {
        setError(copy.errors.rejected);
        setPassword("");
        return;
      }
      await onSignedIn();
    } catch {
      setError(copy.errors.offline);
    } finally {
      setPending(false);
    }
  }

  const emailId = `${idPrefix}-password-email`;
  const passwordId = `${idPrefix}-password`;

  return (
    <div className="flex flex-col gap-nf-ds-6">
      <header className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center">
        <Display as="h1" className="text-balance">
          {copy.headline}
        </Display>
        <Body muted onPaper className="text-pretty">
          {copy.lede}
        </Body>
      </header>

      <form
        className="flex w-full flex-col gap-nf-ds-4"
        onSubmit={(event) => {
          event.preventDefault();
          void signIn();
        }}
      >
        <div className="flex flex-col gap-nf-ds-2">
          <Label htmlFor={emailId}>{copy.emailLabel}</Label>
          <Input
            id={emailId}
            type="email"
            autoComplete="username"
            required
            maxLength={254}
            value={email}
            disabled={pending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-nf-ds-2">
          <Label htmlFor={passwordId}>{copy.passwordLabel}</Label>
          <Input
            id={passwordId}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            disabled={pending}
            aria-invalid={Boolean(error)}
            onChange={(event) => {
              setPassword(event.target.value);
              if (error) setError(null);
            }}
          />
        </div>
        <Button
          type="submit"
          variant="brand"
          disabled={pending || !email.trim() || !password}
        >
          {pending ? copy.signingIn : copy.submit}
        </Button>

        {error && (
          <Alert variant="err" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </form>

      {footer}
    </div>
  );
}
